import { LogoIcon } from '@assets/images';
import Colors from '@constants/Colors';
import { useGetProfile } from '@sunrise-ui/api-client';
import {
  Loader,
  ProfileBasicInfo,
  ProfileImg,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import * as Linking from 'expo-linking';
import React from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import { plainRenderer, QrCodeSvg } from 'react-native-qr-svg';

export function MyQrCode() {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const { currentUserId } = useAuthStore();
  const { data: profile, isLoading: isLoadingProfile } =
    useGetProfile(currentUserId);

  const profileUrl = Linking.createURL(`/to/${currentUserId}`);
  const qrSize = isMobile ? 220 : 280;

  if (isLoadingProfile) {
    return (
      <View style={styles.loadingContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.container}
    >
      <View style={styles.profileSection}>
        <ProfileImg
          userId={currentUserId}
          imgSize="large"
          styleSize={isMobile ? 90 : 120}
          profile={profile}
        />
        <ProfileBasicInfo
          firstName={profile?.first_name}
          lastName={profile?.last_name}
          pronouns={profile?.pronouns}
          headline={profile?.headline}
          size="medium"
          align="center"
          style={styles.profileInfo}
        />
      </View>

      <View style={styles.qrContainer}>
        <QrCodeSvg
          value={profileUrl}
          frameSize={qrSize}
          content={<Image source={LogoIcon} style={styles.logoIcon} />}
          contentStyle={styles.qrContent}
          renderer={{ ...plainRenderer, options: { padding: 0 } }}
          backgroundColor={Colors.dark.purple0}
          dotColor={Colors.dark.tint}
        />
      </View>

      <TextReg style={styles.helpText}>
        Scan to view my profile and connect
      </TextReg>
    </ScrollView>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    scrollView: {
      flex: 1,
    },
    container: {
      flexGrow: 1,
      padding: isMobile ? 16 : 32,
      paddingTop: isMobile ? 24 : 48,
      gap: isMobile ? 20 : 38,
      paddingBottom: isMobile ? 32 : 40,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    profileSection: {
      alignItems: 'center',
      gap: 8,
    },
    profileInfo: {
      alignItems: 'center',
    },
    qrContainer: {
      backgroundColor: Colors.dark.purple0,
      borderRadius: 16,
      padding: isMobile ? 12 : 16,
      justifyContent: 'center',
      alignItems: 'center',
    },
    logoIcon: {
      width: '100%',
      height: '100%',
    },
    qrContent: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? 6 : 10,
    },
    helpText: {
      fontSize: isMobile ? 14 : 16,
      opacity: 0.8,
      textAlign: 'center',
    },
  });
